import { useState, Fragment, ReactNode } from 'react'
import cn from 'clsx'

// Components
import { Image, Link, Section, IconFilledPlayFat, IconArrow, Pill } from '@tracklib/kit'
import VideoModal from 'components/modal/video'

// Hooks
import { useDarkMode } from 'modules/profile/hooks'
import { useTracking } from 'modules/tracking/hooks'

// utils
import { getDocumentUrl } from 'utils'

import styles from './mediaandtext2021.css'

type Text = {
  type: 'text'
  label: string
  title: string
  description: string
  link_text: string
  link_url: string
}

type InlineVideo = {
  type: 'inline_video_block'
  thumbnail: number
  webm: number
  mp4: number
  dark_thumbnail: number
  dark_webm: number
  dark_mp4: number
}

type ThemeImage = {
  type: 'images'
  light: number
  dark: number
  image_width: number
  image_height: number
}

type VideoModal = {
  type: 'video_modal'
  thumbnail: number
  url: string
  play_text: string
  thumbnail_width: number
  thumbnail_height: number
}

type MediaAndTextProps = {
  value: {
    label?: string
    title: string
    description: string
    link_text?: string
    link_url?: string
    text_on_right?: boolean
    media: Array<{
      type: string
      value: ThemeImage | VideoModal | InlineVideo | Text
    }>
  }
}

export const Div = ({ children, className }: { children: ReactNode; className?: string }) => <div className={className}>{children}</div>

const MediaAndText2021 = ({
  value: { label, title, description, link_text, link_url, text_on_right, media },
}: MediaAndTextProps): JSX.Element => {
  const trackEvent = useTracking()
  const isDarkMode = useDarkMode()
  const [videoModalActive, setVideoModalActive] = useState(false)
  const Content = link_url ? Link : Div

  return (
    <Section className={styles.frontpageSection} extraPad>
      <div
        className={cn(styles.wrapper, text_on_right && styles.reversed, media.find(x => x.type === 'text') && styles.withText)}
        id={title === 'Revenue share' ? 'revenueshare' : undefined}
      >
        <div className={styles.media}>
          <div className={styles.mediaInner}>
            <div className={styles.mediaAbs}>
              {media.map(({ type, value }) => {
                const { light, dark, image_width, image_height } = value as ThemeImage
                if (type === 'images') {
                  return (
                    <Fragment key={light}>
                      {isDarkMode && dark ? (
                        <Image className={styles.image} image={dark} alt={title} lazy width={image_width} height={image_height} />
                      ) : (
                        <Image className={styles.image} image={light} alt={title} lazy width={image_width} height={image_height} />
                      )}
                    </Fragment>
                  )
                }
                if (type === 'video_modal') {
                  const { thumbnail, url, play_text, thumbnail_width, thumbnail_height } = value as VideoModal
                  return (
                    <Fragment key={url}>
                      <button
                        key={url}
                        className={styles.videoModalWrapper}
                        onClick={() => {
                          trackEvent('mediaAndTextBlock click', {
                            eventCategory: 'MediaAndTextBlock',
                            eventAction: 'play',
                            eventLabel: url,
                          })
                          return setVideoModalActive(!videoModalActive)
                        }}
                        style={{ paddingBottom: `${(thumbnail_height / thumbnail_width) * 100}%`, height: thumbnail_height ? 0 : 'auto' }}
                      >
                        <Image image={thumbnail} lazy alt={title} width={thumbnail_width} height={thumbnail_height} />
                        <div className={styles.overlay}>
                          <IconFilledPlayFat />
                          {play_text && <p>{play_text}</p>}
                        </div>
                      </button>
                      <VideoModal videoUrl={url} isOpen={videoModalActive} closeModal={() => setVideoModalActive(!videoModalActive)} />
                    </Fragment>
                  )
                }
                if (type === 'inline_video_block') {
                  const { thumbnail, dark_thumbnail, mp4, webm, dark_mp4, dark_webm } = value as InlineVideo
                  if (!webm && !mp4) {
                    return null
                  }
                  return (
                    <Fragment key={thumbnail}>
                      <video autoPlay muted loop playsInline className={styles.video}>
                        {webm && (
                          <source src={isDarkMode && dark_webm ? getDocumentUrl(dark_webm) : getDocumentUrl(webm)} type="video/webm" />
                        )}
                        {mp4 && <source src={isDarkMode && dark_mp4 ? getDocumentUrl(dark_mp4) : getDocumentUrl(mp4)} type="video/mp4" />}
                        <Image image={isDarkMode && dark_thumbnail ? dark_thumbnail : thumbnail} absolute lazy alt={title} />
                      </video>
                    </Fragment>
                  )
                }
                if (type === 'text') {
                  const {
                    label: textLabel,
                    link_url: textLinkUrl,
                    link_text: textLinkText,
                    title: textTitle,
                    description: textDesc,
                  } = value as Text
                  const TextContent = textLinkUrl ? Link : Div
                  return (
                    <div key={type}>
                      {textLabel && <Pill>{textLabel}</Pill>}
                      <TextContent to={textLinkUrl}>
                        <h2 className={styles.title}>{textTitle}</h2>
                        <p className={styles.description}>{textDesc}</p>
                        {textLinkText && (
                          <p className={styles.fakeLink}>
                            {textLinkText} <IconArrow className={styles.arrowRight} />
                          </p>
                        )}
                      </TextContent>
                    </div>
                  )
                }
                return 'unsupported block'
              })}
            </div>
          </div>
        </div>
        <div className={styles.content}>
          {label ? <Pill>{label}</Pill> : <span className={styles.mobileSpacer} />}
          <Content
            to={link_url}
            onClick={() => {
              if (link_url) {
                trackEvent('mediaAndTextBlock click', {
                  eventCategory: 'MediaAndTextBlock',
                  eventAction: 'click',
                  eventLabel: title,
                })
              }
            }}
            className={styles.contentInner}
          >
            <h2 className={styles.title}>{title}</h2>
            <p className={styles.description}>{description}</p>
            {link_text && (
              <p className={styles.fakeLink}>
                {link_text} <IconArrow className={styles.arrowRight} />
              </p>
            )}
          </Content>
        </div>
      </div>
    </Section>
  )
}

export default MediaAndText2021
